import { useEffect, useState } from "react";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import { getAuthHeaders } from "../utils/authUtils";
import { generatePageNumbers } from "../utils/pagination";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;

export const Notices = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 1,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.title = "Notices - Smart Scholars 360";
    AOS.init({ duration: 800, once: false });
    fetchNotices();
  }, []);

  const fetchNotices = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_URI}/entities?category=notice&page=${page}&limit=${pagination.page_size}`,
        getAuthHeaders(),
      );

      if (res.data && res.data.data) {
        const transformedData = res.data.data.map((item) => ({
          id: item.id,
          day: item.data.day,
          month_year: item.data.month_year,
          message: item.data.message,
          is_new: item.data.is_new,
        }));
        setNotices(transformedData);
        setPagination({
          page: res.data.page,
          page_size: res.data.limit,
          total: res.data.total,
          total_pages: Math.ceil(res.data.total / res.data.limit),
        });
      } else {
        setNotices([]);
      }
      setError("");
    } catch (err) {
      setError("Failed to load notices. Showing default content.");
      setNotices([
        {
          id: 1,
          day: "3",
          month_year: "June 2025",
          message:
            "Admission forms are available from 5th June. Visit the admin office for details.",
          is_new: true,
        },
        {
          id: 2,
          day: "27",
          month_year: "May 2025",
          message:
            "Final semester results will be declared on 31st May. Check the notice board.",
          is_new: false,
        },
      ]);
      setPagination({ page: 1, page_size: 10, total: 2, total_pages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages && !loading) {
      fetchNotices(newPage);
      document
        .querySelector(".notice_body")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const NoticeSkeleton = () => (
    <div className="notices mt-3" style={{ opacity: 0.7 }}>
      <div className="notice_date">
        {[
          { w: "40px", h: "20px" },
          { w: "30px", h: "24px" },
          { w: "60px", h: "14px" },
        ].map(({ w, h }, i) => (
          <div
            key={i}
            style={{
              width: w,
              height: h,
              borderRadius: "4px",
              marginBottom: "8px",
              background:
                "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.5s infinite",
            }}
          />
        ))}
      </div>
      <div className="notice_info" style={{ width: "100%" }}>
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            style={{
              display: "block",
              width: i === 2 ? "80%" : "100%",
              height: "16px",
              borderRadius: "4px",
              marginBottom: "8px",
              background:
                "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.5s infinite",
            }}
          />
        ))}
      </div>
    </div>
  );

  return (
    <>
      <div className="ss-inner-hero">
        <div className="container">
          <h1>Notice Board</h1>
          <p>Important announcements and updates for students and parents</p>
        </div>
      </div>
      <section className="notice_body">
        <div className="container">
          <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
          <div className="col">
            <div className="notice_box">
              <h3>Notice Board</h3>

              {error && (
                <div className="alert alert-warning" role="alert">
                  {error}
                </div>
              )}

              <div className="notice_inner_box">
                {loading && notices.length === 0 && (
                  <>
                    {Array.from({ length: 10 }).map((_, i) => (
                      <NoticeSkeleton key={i} />
                    ))}
                  </>
                )}

                {loading && notices.length > 0 && (
                  <>
                    {Array.from({ length: pagination.page_size }).map(
                      (_, i) => (
                        <NoticeSkeleton key={i} />
                      ),
                    )}
                  </>
                )}

                {!loading && notices.length === 0 && (
                  <div style={{ textAlign: "center", padding: "50px" }}>
                    <h5>No notices available</h5>
                    <p>Check back later for new notices!</p>
                  </div>
                )}

                {!loading &&
                  notices.map((notice) => (
                    <div className="notices mt-3" key={notice.id}>
                      <div className="notice_date">
                        {notice.is_new && (
                          <span className="badge text-bg-primary new_notice">
                            New
                          </span>
                        )}
                        <p>
                          {notice.day || "N/A"}
                          <span>{notice.month_year || "No Date"}</span>
                        </p>
                      </div>
                      <div className="notice_info">
                        <p>{notice.message || "No message available."}</p>
                      </div>
                    </div>
                  ))}
              </div>

              {pagination.total_pages > 1 && (
                <nav className="mt-4">
                  <ul className="pagination justify-content-center">
                    <li
                      className={`page-item ${
                        pagination.page === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={loading}
                      >
                        <i className="bi bi-chevron-left"></i> Previous
                      </button>
                    </li>

                    {generatePageNumbers(
                      pagination.page,
                      pagination.total_pages,
                    ).map((page, i) => (
                      <li
                        key={i}
                        className={`page-item ${
                          page === pagination.page ? "active" : ""
                        } ${page === "..." ? "disabled" : ""}`}
                      >
                        {page === "..." ? (
                          <span className="page-link">...</span>
                        ) : (
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(page)}
                            disabled={loading}
                          >
                            {page}
                          </button>
                        )}
                      </li>
                    ))}

                    <li
                      className={`page-item ${
                        pagination.page === pagination.total_pages
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={loading}
                      >
                        Next <i className="bi bi-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
              {!loading && notices.length > 0 && (
                <div style={{ textAlign: "center", marginTop: "20px" }}>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => fetchNotices(pagination.page)}
                    disabled={loading}
                  >
                    {loading ? "Refreshing..." : "Refresh Notices"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
